import React from 'react';

import {assert} from 'Asserts';
import {useActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import AssayGroup, {AssayGroupProps} from './assay-group/AssayGroup';
import AssayGroupContainer from './assay-group/AssayGroupContainer';
import EmptyAssayGroup from './assay-group/EmptyAssayGroup';
import {DisplayConfig} from './AttachmentConstants';

export interface NoteLinkedAssayGroupProps extends Omit<AssayGroupProps, 'assayRun'> {
  refreshState: {isRefreshing: boolean; refreshCounter: number};
  assayRuns: Array<AssayRun> | null;
  handleRefreshVersionedData: () => Promise<void>;
  handleSetAssayRun: (
    selectedRunId: string,
    assayResultSchemas: Array<AssaySchema>,
    runSchemaId: string,
    runDisplayConfig: DisplayConfig
  ) => Promise<void>;
  // Called to backfill a provenanceUuid, if needed
  handleSetProvenanceUuid: (provenanceUuid: string) => void;
}

/**
 * Component that renders a run together with its linked results in an entry.
 *
 * Supports empty state when assayRunSchema is available but assayRunId is not.
 */
export function NoteLinkedAssayGroup(props: NoteLinkedAssayGroupProps): JSX.Element {
  const titleText = props.assayRunSchemaInfo.versionedName || props.assayRunSchemaInfo.assayRunSchema.name;
  // Uses action function context hook to get creation context for creating a new EmptyAssayGroup
  const actionFunctionContext = useActionFunctionContext();
  const isLoading = props.assayRunInfo.assayRunId != null && props.assayRuns == null;
  if (isLoading || props.refreshState.isRefreshing) {
    return (
      <AssayGroupContainer
        assayRunIdForValidationFetch={props.assayRunInfo.assayRunId}
        indentation={props.indentation}
        title={titleText}
      >
        <div className="u-text-align-center">
          <HorizontalThrobber />
        </div>
      </AssayGroupContainer>
    );
  }

  const runCount = props.assayRuns == null ? 0 : props.assayRuns.length;
  assert(runCount <= 1, 'NoteLinkedAssayGroup was passed multiple runs but can only display a single one');

  if (runCount === 0) {
    return (
      <EmptyAssayGroup
        assayRunSchemaInfo={props.assayRunSchemaInfo}
        assayRunInfo={props.assayRunInfo}
        handleUpdateTags={props.handleUpdateTags}
        cid={props.cid}
        entryId={props.fileId}
        provenanceUuid={props.provenanceUuid}
        handleSetAssayRun={props.handleSetAssayRun}
        handleSetProvenanceUuid={props.handleSetProvenanceUuid}
        indentation={props.indentation}
        locked={props.locked}
        actionFunctionContext={actionFunctionContext}
        viewer={props.viewer}
      />
    );
  }

  const [assayRun] = props.assayRuns!;
  return (
    <AssayGroup
      assayRun={assayRun}
      assayRunSchemaInfo={props.assayRunSchemaInfo}
      assayRunInfo={props.assayRunInfo}
      versionedStructuredTables={props.versionedStructuredTables}
      automationFiles={props.automationFiles}
      handleRemoveAssayRun={props.handleRemoveAssayRun}
      resultSchemaToCount={props.resultSchemaToCount}
      review={props.review}
      /* AssayResultsContainer props */
      assayResults={props.assayResults}
      assayResultSchemas={props.assayResultSchemas}
      childrenFetching={props.childrenFetching}
      dataTables={props.dataTables}
      displayConfigBySchemaId={props.displayConfigBySchemaId}
      editor={props.editor}
      editorProps={props.editorProps}
      handleRefreshVersionedData={props.handleRefreshVersionedData}
      handleUpdateNoteLinkedAssayData={props.handleUpdateNoteLinkedAssayData}
      handleUpdateTags={props.handleUpdateTags}
      handleUpdateAutomationFiles={props.handleUpdateAutomationFiles}
      usedTableNames={props.usedTableNames}
      provenanceUuid={props.provenanceUuid}
      /* Proxied note props */
      cid={props.cid}
      fileId={props.fileId}
      indentation={props.indentation}
      index={props.index}
      locked={props.locked}
      viewer={props.viewer}
      refreshCounter={props.refreshState.refreshCounter}
    />
  );
}

export default NoteLinkedAssayGroup;
